/**
 * SoloMultiplayerButton — floating "live tables" button shown during Solo
 * Grind. Tapping it leaves solo and drops into the lobby browser to join a
 * real cee-lo / craps room. A badge shows how many tables are live right now,
 * and the button pulses when a new one opens — so a solo grinder gets pulled
 * toward multiplayer the moment someone else is around.
 *
 * Single always-mounted overlay on document.body; `.visible` shows it (solo
 * only), `.has-rooms` lights it up when tables exist.
 */

class SoloMultiplayerButtonImpl {
  private root: HTMLElement | null = null;
  private badge: HTMLElement | null = null;
  private onTap: (() => void) | null = null;
  private lastCount = 0;
  private suppressed = false;

  private ensure(): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('button');
    root.className = 'solo-mp-btn';
    root.innerHTML = `
      <span class="solo-mp-dot"></span>
      <span class="solo-mp-label">TABLES</span>
      <span class="solo-mp-badge" hidden>0</span>`;
    root.addEventListener('click', () => this.onTap?.());
    document.body.appendChild(root);

    this.root = root;
    this.badge = root.querySelector('.solo-mp-badge');
  }

  /** Show the button (solo only). `onTap` fires on click. */
  show(onTap: () => void): void {
    this.ensure();
    this.onTap = onTap;
    const root = this.root!;
    root.classList.toggle('suppressed', this.suppressed);
    void root.offsetHeight; // commit hidden base before fade-in
    root.classList.add('visible');
  }

  hide(): void {
    this.onTap = null;
    this.root?.classList.remove('visible');
  }

  /**
   * Temporarily fade the button out without tearing it down (used while the
   * solo game-select modal is up, whose chrome occupies the same top strip).
   * Unlike hide(), keeps the tap callback and count intact.
   */
  setSuppressed(on: boolean): void {
    this.suppressed = on;
    this.root?.classList.toggle('suppressed', on);
  }

  /** Update the live-table count. Pulses when the count rises. */
  setRoomCount(count: number): void {
    if (!this.root || !this.badge) return;
    const rose = count > this.lastCount;
    this.lastCount = count;

    this.root.classList.toggle('has-rooms', count > 0);
    if (count > 0) {
      this.badge.textContent = String(count);
      this.badge.hidden = false;
    } else {
      this.badge.hidden = true;
    }

    if (rose) {
      this.root.classList.remove('pulse');
      void this.root.offsetHeight; // restart the animation
      this.root.classList.add('pulse');
    }
  }

  private injectStyles(): void {
    if (document.getElementById('solo-mp-styles')) return;
    const style = document.createElement('style');
    style.id = 'solo-mp-styles';
    style.textContent = `
      .solo-mp-btn {
        position: fixed; top: calc(env(safe-area-inset-top, 0px) + 14px); left: 14px;
        z-index: 60;
        display: flex; align-items: center; gap: 7px;
        padding: 8px 13px; border-radius: 22px;
        background: linear-gradient(160deg, rgba(28,28,34,0.94), rgba(16,16,20,0.94));
        border: 2px solid rgba(255,255,255,0.14);
        color: #C8C8D0; font-family: 'Bebas Neue', Impact, sans-serif;
        font-size: 16px; letter-spacing: 1.2px; cursor: pointer;
        opacity: 0; transform: translateY(-6px); pointer-events: none;
        transition: opacity 220ms ease, transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease, color 220ms ease;
      }
      .solo-mp-btn.visible { opacity: 1; transform: translateY(0); pointer-events: auto; }
      .solo-mp-btn.visible.suppressed { opacity: 0; transform: translateY(-6px); pointer-events: none; }
      .solo-mp-btn.has-rooms {
        border-color: rgba(0,255,136,0.6); color: #EAFBF1;
        box-shadow: 0 0 16px rgba(0,255,136,0.35);
      }
      .solo-mp-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: #6A6A72; transition: background 220ms ease, box-shadow 220ms ease;
      }
      .solo-mp-btn.has-rooms .solo-mp-dot {
        background: #00FF88; box-shadow: 0 0 8px rgba(0,255,136,0.8);
      }
      .solo-mp-badge {
        min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
        background: #00FF88; color: #08080A; font-size: 13px;
        display: flex; align-items: center; justify-content: center;
      }
      /* The class's display:flex beats the UA [hidden] rule, so hiding via
         the hidden attribute needs this explicit override (same landmine as
         the dice-purchase gold button). */
      .solo-mp-badge[hidden] { display: none; }
      @keyframes solo-mp-pulse {
        0% { transform: translateY(0) scale(1); }
        40% { transform: translateY(0) scale(1.12); }
        100% { transform: translateY(0) scale(1); }
      }
      .solo-mp-btn.pulse { animation: solo-mp-pulse 420ms ease; }
    `;
    document.head.appendChild(style);
  }
}

/** Always-mounted singleton (created on first show). */
export const soloMultiplayerButton = new SoloMultiplayerButtonImpl();
